import { useRef, useEffect, useState, useCallback } from 'react'
import styles from './ToggleGroup.module.scss'

export interface ToggleOption<T extends string = string> {
    value: T
    label: string
    count?: number
    /** Optional color variant for when active */
    color?: 'default' | 'live' | 'upcoming-soon' | 'upcoming' | 'ended' | 'suspended' | 'problem'
    /** Optional description shown below the toggle group when this option is selected */
    description?: string
}

interface ToggleGroupProps<T extends string = string> {
    options: ToggleOption<T>[]
    value: T
    onChange: (value: T) => void
    /** "buttons" (default) renders each option as a distinct bordered button.
     *  "toggle" wraps all options in a single container with a segmented-control look. */
    variant?: 'buttons' | 'toggle'
    size?: 'default' | 'small'
    className?: string
}

/**
 * ToggleGroup - A group of mutually exclusive toggle buttons (radio behavior)
 * 
 * Use for filtering, view switching, or any single-selection from a small set of options.
 * Optionally displays a description below the buttons based on the selected option.
 * When descriptions are present, they align horizontally with the selected button.
 */
export function ToggleGroup<T extends string = string>({
    options,
    value,
    onChange,
    variant = 'buttons',
    size = 'default',
    className = '',
}: ToggleGroupProps<T>) {
    const selectedOption = options.find(opt => opt.value === value)
    const hasDescriptions = options.some(opt => opt.description)
    
    const containerRef = useRef<HTMLDivElement>(null)
    const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map())
    const [descriptionOffset, setDescriptionOffset] = useState(0)

    const setButtonRef = useCallback((value: T, el: HTMLButtonElement | null) => {
        if (el) {
            buttonRefs.current.set(value, el)
        } else {
            buttonRefs.current.delete(value)
        }
    }, [])

    // Calculate the offset for the description based on the selected button position
    useEffect(() => {
        if (!hasDescriptions) return

        const updateOffset = () => {
            const container = containerRef.current
            const selectedButton = buttonRefs.current.get(value)
            
            if (!container || !selectedButton) {
                setDescriptionOffset(0)
                return
            }

            const containerRect = container.getBoundingClientRect()
            const buttonRect = selectedButton.getBoundingClientRect()
            
            // Calculate offset relative to container
            const offset = buttonRect.left - containerRect.left
            setDescriptionOffset(offset)
        }

        updateOffset()
        
        // Recalculate on window resize
        window.addEventListener('resize', updateOffset)
        return () => window.removeEventListener('resize', updateOffset)
    }, [value, hasDescriptions])

    return (
        <div ref={containerRef} className={`${styles.toggleGroupContainer} ${className}`}>
            <div className={`${styles.toggleGroup} ${variant === 'toggle' ? styles.toggle : ''}`}>
                {options.map((option) => {
                    const isActive = value === option.value
                    const colorClass = option.color ? styles[option.color] : ''

                    return (
                        <button
                            key={option.value}
                            ref={(el) => setButtonRef(option.value, el)}
                            type="button"
                            className={`${styles.toggleButton} ${isActive ? styles.active : ''} ${colorClass} ${size === 'small' ? styles.small : ''}`}
                            onClick={() => onChange(option.value)}
                        >
                            {option.label}
                            {option.count !== undefined && (
                                <span className={styles.count}>{option.count}</span>
                            )}
                        </button>
                    )
                })}
            </div>
            {hasDescriptions && (
                <p 
                    className={styles.description}
                    style={{ marginLeft: descriptionOffset }}
                >
                    {selectedOption?.description}
                </p>
            )}
        </div>
    )
}

